import { useState, useEffect, useRef } from 'react'
import { ChevronDown, Printer, Map, RefreshCw } from 'lucide-react'
import leiloesService from '../../services/leiloesService'
import mapasService from '../../services/mapasService'
import { useOrganizador } from '../../contexts/OrganizadorContext'

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:8001').replace(/\/$/, '')
const brl = v => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)
const fmtNum = v => v != null && v !== 0 ? new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v) : '0,00'
const fmtDate = s => { if (!s) return ''; try { return new Date(s).toLocaleDateString('pt-BR') } catch { return s } }

function Select({ label, value, onChange, children, disabled, className = '' }) {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {label && <label className="text-xs text-gray-500 font-medium">{label}</label>}
      <div className="relative">
        <select value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
          className="w-full appearance-none border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400">
          {children}
        </select>
        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      </div>
    </div>
  )
}

const S = {
  page: { fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 12, color: '#000', background: '#fff', padding: '20px 24px 24px' },
  hdr: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '2px solid #1f2937', paddingBottom: 12, marginBottom: 16 },
  hdrLogo: { maxHeight: 70, maxWidth: 160, objectFit: 'contain' },
  hdrCenter: { flex: 1, textAlign: 'center', padding: '0 16px' },
  hdrT1: { fontSize: 18, fontWeight: 700 },
  hdrT2: { fontSize: 12, color: '#374151', marginTop: 2 },
  hdrT3: { fontSize: 12, fontWeight: 600, color: '#1f2937', marginTop: 2 },
  vendTitle: { background: '#d9d9d9', padding: '6px 12px', fontWeight: 700, fontSize: 13, textAlign: 'center', border: '1px solid #000', marginTop: 16 },
  tbl: { width: '100%', borderCollapse: 'collapse', fontSize: 11, border: '1px solid #000' },
  th: { border: '1px solid #999', padding: '4px 6px', background: '#f0f0f0', fontWeight: 700, fontSize: 10, textAlign: 'left' },
  thC: { border: '1px solid #999', padding: '4px 6px', background: '#f0f0f0', fontWeight: 700, fontSize: 10, textAlign: 'center' },
  thR: { border: '1px solid #999', padding: '4px 6px', background: '#f0f0f0', fontWeight: 700, fontSize: 10, textAlign: 'right' },
  td: { border: '1px solid #ccc', padding: '3px 6px', fontSize: 11 },
  tdC: { border: '1px solid #ccc', padding: '3px 6px', fontSize: 11, textAlign: 'center' },
  tdR: { border: '1px solid #ccc', padding: '3px 6px', fontSize: 11, textAlign: 'right' },
  resumoTitle: { background: '#f0f0f0', padding: '3px 6px', fontWeight: 700, fontSize: 10, textAlign: 'center', border: '1px solid #000' },
  totalRow: { background: '#f0f0f0', fontWeight: 700 },
  footer: { textAlign: 'left', fontSize: 10, color: '#6b7280', marginTop: 20, paddingTop: 8, borderTop: '1px solid #e5e7eb' },
}

function VendedorSection({ vendedor }) {
  const lotes = vendedor.lotes || []
  const vendas = lotes.filter(l => l.situacao === 'VENDA')
  const defesas = lotes.filter(l => l.situacao === 'DEFESA')
  const naoMov = lotes.filter(l => l.situacao === 'NAO_MOV')

  const calcResumo = (grupo) => {
    const machos = grupo.filter(l => l.sexo === 'Macho').reduce((s, l) => s + l.qtd, 0)
    const femeas = grupo.filter(l => l.sexo === 'Fêmea').reduce((s, l) => s + l.qtd, 0)
    const outros = grupo.filter(l => l.sexo !== 'Macho' && l.sexo !== 'Fêmea').reduce((s, l) => s + l.qtd, 0)
    const qtdTotal = grupo.reduce((s, l) => s + l.qtd, 0)
    const lotesTotal = new Set(grupo.map(l => l.numero_lote)).size
    const valorTotal = grupo.reduce((s, l) => s + l.valor_total, 0)
    const media = qtdTotal > 0 ? valorTotal / qtdTotal : 0
    return { machos, femeas, outros, qtdTotal, lotesTotal, valorTotal, media }
  }

  const rVendas = calcResumo(vendas)
  const rDefesas = calcResumo(defesas)
  const rNaoMov = calcResumo(naoMov)
  const total = {
    machos: rVendas.machos + rDefesas.machos + rNaoMov.machos,
    femeas: rVendas.femeas + rDefesas.femeas + rNaoMov.femeas,
    outros: rVendas.outros + rDefesas.outros + rNaoMov.outros,
    qtdTotal: rVendas.qtdTotal + rDefesas.qtdTotal + rNaoMov.qtdTotal,
    lotesTotal: rVendas.lotesTotal + rDefesas.lotesTotal + rNaoMov.lotesTotal,
    valorTotal: rVendas.valorTotal + rDefesas.valorTotal + rNaoMov.valorTotal,
    media: (rVendas.qtdTotal + rDefesas.qtdTotal + rNaoMov.qtdTotal) > 0
      ? (rVendas.valorTotal + rDefesas.valorTotal + rNaoMov.valorTotal) / (rVendas.qtdTotal + rDefesas.qtdTotal + rNaoMov.qtdTotal) : 0,
  }

  return (
    <div>
      <div style={S.vendTitle}>Vendedor: {vendedor.nome?.toUpperCase()}</div>
      <table style={S.tbl}>
        <thead>
          <tr>
            <th style={S.thC}>LOTE</th>
            <th style={S.th}>COMPRADOR</th>
            <th style={S.thC}>QTD</th>
            <th style={S.thC}>PESO</th>
            <th style={S.thC}>RAÇA</th>
            <th style={S.thC}>SX</th>
            <th style={S.thR}>UNIT.</th>
            <th style={S.thR}>TOTAL</th>
          </tr>
        </thead>
        <tbody>
          {lotes.map((l, i) => (
            <tr key={i}>
              <td style={S.tdC}>{l.numero_lote}</td>
              <td style={S.td}>{l.comprador}</td>
              <td style={S.tdC}>{l.qtd}</td>
              <td style={S.tdC}>{l.peso ? fmtNum(l.peso) : ''}</td>
              <td style={S.tdC}>{l.raca}</td>
              <td style={S.tdC}>{l.sexo_abrev}</td>
              <td style={S.tdR}>{fmtNum(l.valor_unitario)}</td>
              <td style={S.tdR}>{fmtNum(l.valor_total)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={S.resumoTitle}>RESUMO DO VENDEDOR</div>
      <table style={S.tbl}>
        <thead>
          <tr>
            <th style={S.th}></th>
            <th style={S.thC}>QTD MACHOS</th>
            <th style={S.thC}>QTD FÊMEAS</th>
            <th style={S.thC}>QTD OUTROS</th>
            <th style={S.thC}>QTD TOTAL</th>
            <th style={S.thC}>LOTES TOTAL</th>
            <th style={S.thR}>VALOR TOTAL</th>
            <th style={S.thR}>MÉDIA ANIMAL</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={{ ...S.td, fontWeight: 600 }}>Resumo Vendas:</td>
            <td style={S.tdC}>{rVendas.machos}</td>
            <td style={S.tdC}>{rVendas.femeas}</td>
            <td style={S.tdC}>{rVendas.outros}</td>
            <td style={S.tdC}>{rVendas.qtdTotal}</td>
            <td style={S.tdC}>{rVendas.lotesTotal}</td>
            <td style={S.tdR}>{fmtNum(rVendas.valorTotal)}</td>
            <td style={S.tdR}>{fmtNum(rVendas.media)}</td>
          </tr>
          <tr>
            <td style={{ ...S.td, fontWeight: 600 }}>Resumo Defesas:</td>
            <td style={S.tdC}>{rDefesas.machos}</td>
            <td style={S.tdC}>{rDefesas.femeas}</td>
            <td style={S.tdC}>{rDefesas.outros}</td>
            <td style={S.tdC}>{rDefesas.qtdTotal}</td>
            <td style={S.tdC}>{rDefesas.lotesTotal}</td>
            <td style={S.tdR}>{fmtNum(rDefesas.valorTotal)}</td>
            <td style={S.tdR}>{fmtNum(rDefesas.media)}</td>
          </tr>
          <tr>
            <td style={{ ...S.td, fontWeight: 600 }}>Resumo Ñ Mov.:</td>
            <td style={S.tdC}>{rNaoMov.machos}</td>
            <td style={S.tdC}>{rNaoMov.femeas}</td>
            <td style={S.tdC}>{rNaoMov.outros}</td>
            <td style={S.tdC}>{rNaoMov.qtdTotal}</td>
            <td style={S.tdC}>{rNaoMov.lotesTotal}</td>
            <td style={S.tdR}>{fmtNum(rNaoMov.valorTotal)}</td>
            <td style={S.tdR}>{fmtNum(rNaoMov.media)}</td>
          </tr>
          <tr style={S.totalRow}>
            <td style={{ ...S.td, fontWeight: 700 }}>Total Geral:</td>
            <td style={{ ...S.tdC, fontWeight: 700 }}>{total.machos}</td>
            <td style={{ ...S.tdC, fontWeight: 700 }}>{total.femeas}</td>
            <td style={{ ...S.tdC, fontWeight: 700 }}>{total.outros}</td>
            <td style={{ ...S.tdC, fontWeight: 700 }}>{total.qtdTotal}</td>
            <td style={{ ...S.tdC, fontWeight: 700 }}>{total.lotesTotal}</td>
            <td style={{ ...S.tdR, fontWeight: 700 }}>{fmtNum(total.valorTotal)}</td>
            <td style={{ ...S.tdR, fontWeight: 700 }}>{fmtNum(total.media)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

export default function MapaGeralVendedores() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id

  const [leiloes, setLeiloes] = useState([])
  const [leilaoId, setLeilaoId] = useState('')
  const [dados, setDados] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loadingL, setLoadingL] = useState(true)
  const [loadingPdf, setLoadingPdf] = useState(false)
  const [pdfUrl, setPdfUrl] = useState(null)
  const [showPdf, setShowPdf] = useState(false)
  const iframeRef = useRef(null)

  useEffect(() => {
    if (!orgId) return
    setLoadingL(true)
    leiloesService.getByOrganizador(orgId)
      .then(data => {
        const sorted = [...(data || [])].sort((a, b) => new Date(b.data_leilao) - new Date(a.data_leilao))
        setLeiloes(sorted)
        if (sorted.length) setLeilaoId(String((sorted.find(l => l.status === 'on') || sorted[0]).id))
      })
      .finally(() => setLoadingL(false))
  }, [orgId])

  useEffect(() => {
    if (!orgId || !leilaoId) { setDados(null); return }
    setLoading(true)
    mapasService.getMapaGeralVendedoresDados(orgId, leilaoId)
      .then(setDados)
      .catch(() => setDados(null))
      .finally(() => setLoading(false))
  }, [orgId, leilaoId])

  const leilaoAtual = leiloes.find(l => String(l.id) === leilaoId)
  const logoOrgSrc = organizadorAtivo?.logo_url
    ? (organizadorAtivo.logo_url.startsWith('http') ? organizadorAtivo.logo_url
        : `${API_BASE}${organizadorAtivo.logo_url.startsWith('/') ? '' : '/'}${organizadorAtivo.logo_url}`)
    : null

  const handleImprimir = async () => {
    setLoadingPdf(true)
    try {
      const blob = await mapasService.getMapaGeralVendedoresPdf(orgId, leilaoId)
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }))
      setPdfUrl(url)
      setShowPdf(true)
    } catch (err) { alert('Erro: ' + (err.response?.statusText || err.message)) }
    finally { setLoadingPdf(false) }
  }
  const closePdf = () => { setShowPdf(false); if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null) } }
  const printPdf = () => { if (iframeRef.current?.contentWindow) { iframeRef.current.contentWindow.focus(); iframeRef.current.contentWindow.print() } }

  if (!orgId) return <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">Selecione um organizador.</div>

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <Map className="w-5 h-5 text-emerald-600" />
        <h1 className="text-lg font-semibold text-gray-800">Mapa Geral Vendedores</h1>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-3">
          <Select label="Leilão" value={leilaoId} onChange={v => { setLeilaoId(v); setDados(null) }} disabled={loadingL} className="flex-[2] min-w-[200px]">
            {loadingL ? <option>Carregando...</option> : leiloes.map(l => <option key={l.id} value={String(l.id)}>{l.nome}</option>)}
          </Select>
          <div className="flex items-end gap-2 ml-auto">
            {loading && <RefreshCw size={15} className="text-gray-400 animate-spin mb-2" />}
            <button onClick={handleImprimir} disabled={!dados || !dados.vendedores?.length || loadingPdf}
              className="flex items-center gap-2 px-4 py-2 h-9 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors">
              <Printer size={15} /> Imprimir
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
      ) : !dados || !dados.vendedores?.length ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400 text-sm">Nenhum vendedor encontrado</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div style={S.page}>
            <div style={S.hdr}>
              <div style={{ minWidth: 140 }}>{logoOrgSrc ? <img src={logoOrgSrc} alt="logo" style={S.hdrLogo} /> : <span style={{ fontWeight: 700 }}>{organizadorAtivo?.name || ''}</span>}</div>
              <div style={S.hdrCenter}>
                <div style={S.hdrT1}>MAPA GERAL DE LOTES</div>
                <div style={S.hdrT2}>{organizadorAtivo?.name || organizadorAtivo?.nome || ''}</div>
                <div style={S.hdrT3}>{leilaoAtual?.nome} — {fmtDate(leilaoAtual?.data_leilao)}</div>
              </div>
              <div style={{ minWidth: 140, textAlign: 'right' }}><img src="/assets/rematei.png" alt="Rematei" style={S.hdrLogo} /></div>
            </div>

            {dados.vendedores.map((vend, i) => (
              <VendedorSection key={vend.id || i} vendedor={vend} />
            ))}

            <div style={S.footer}>www.rematei.com.br</div>
          </div>
        </div>
      )}

      {showPdf && pdfUrl && (
        <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-50 sm:p-4">
          <div className="bg-white w-full max-w-5xl flex flex-col rounded-t-2xl sm:rounded-xl shadow-2xl" style={{ height: '92dvh' }}>
            <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-2xl sm:rounded-t-xl flex-shrink-0">
              <span className="font-semibold text-gray-800 text-sm truncate flex-1 min-w-0">Mapa Geral Vendedores — {leilaoAtual?.nome}</span>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button onClick={printPdf} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"><Printer size={15} /> Imprimir</button>
                <button onClick={closePdf} className="px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm">Fechar</button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden rounded-b-xl"><iframe ref={iframeRef} src={pdfUrl} title="Mapa Geral Vendedores" style={{ width: '100%', height: '100%', border: 'none' }} /></div>
          </div>
        </div>
      )}
    </div>
  )
}
